import './Events.scss';
import ActionMenu from '../../component/actionmenu/ActionMenu';
import {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState
} from 'react';
import {
    NavLink,
    useLocation,
    useNavigate
} from 'react-router-dom';
import DataTable, {
    FILTER_TYPES,
    DataTableContext,
    DataTableProvider,
} from '../../component/datatable2';
import { Column } from 'primereact/column';
import { RenderedHelper } from '../../utils/render/RenderedHelper';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n';
import { MessageContext } from '../../context/MessageContext';
import { EventService } from '../../service/EventService';
import moment from 'moment';
import { Constants } from '../../utils/Constants';
import useNavigationContext from '../../utils/navigationcontext/NavigationContext';
import { TabPanel, TabView } from 'primereact/tabview';
import { EventConfigDataTableContext } from "../../component/datatable/DataTableContext";
import ActionButton from '../../component/actionbutton/ActionButton';
import ConfirmDialog from '../../component/confirmdialog/ConfirmDialog';
import { EventConfigService } from '../../service/EventConfigService';
import { LocationService } from '../../service/LocationService';
import { DeviceService } from '../../service/DeviceService';
import EventConfigSidebar from './EventConfigSidebar';
import { useAuth } from '../../hook/useAuth';

const EVENTS_TAB_ROUTE_BY_INDEX = {
    0: '/events',
    1: '/events/archive',
    2: '/events/configuration'
};

const EVENTS_TAB_INDEX_BY_ROUTE = {
    '/events': 0,
    '/events/archive': 1,
    '/events/configuration': 2
};

const isModifiedLinkClick = (event) => {
    return (
        event.button !== 0 ||
        event.ctrlKey ||
        event.metaKey ||
        event.shiftKey ||
        event.altKey
    );
};

const Events = () => {
    return (
        <DataTableProvider>
            <EventsImpl />
        </DataTableProvider>
    );
};
const EventsImpl = () => {
    const menuRef = useRef(null);
    const tableRef = useRef(null);
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const { searchCriteria, setSearchCriteria } = useContext(DataTableContext);
    const { showErrorMessage, showSuccessMessage } = useContext(MessageContext);
    const { isUserInAnyRole } = useAuth();
    const [eventTypeOptions, setEventTypeOptions] = useState([]);
    const [technicianOptions, setTechnicianOptions] = useState([]);
    const [locationOptions, setLocationOptions] = useState([]);
    const [deviceOptions, setDeviceOptions] = useState([]);
    const [eventPriorityOptions, setEventPriorityOptions] = useState([]);
    const [eventStatusOptions, setEventStatusOptions] = useState([]);
    const [eventConfigTypeList, setEventConfigTypeList] = useState([]);
    const [eventConfigPriorityList, setEventConfigPriorityList] = useState([]);
    const activeTab =
        EVENTS_TAB_INDEX_BY_ROUTE[location.pathname] ?? 0;
    const [eventConfigStatuses, setEventConfigStatuses] = useState([]);
    const [sidebarState, setSidebarState] = useState({ visible: false, eventConfigId: null });
    const [deactivateDialogState, setDeactivateDialogState] = useState({ visible: false, eventConfigId: null });
    const [activateDialogState, setActivateDialogState] = useState({ visible: false, eventConfigId: null });

    const apiValueFromConverter = useMemo(() =>
        value => moment(value).startOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET),
        []
    );
    const apiValueToConverter = useMemo(() =>
        value => moment(value).endOf('day').utc().format(Constants.DATE_TIME_FORMAT_GET),
        []
    );
    const apiValueConverter = useMemo(() =>
        value => value.map(v => v.id).join(','),
        []
    );

    const defaultCriteria = useMemo(() => ({
        first: 0,
        rows: 25,
        sortField: 'eventDateTime',
        sortOrder: -1,
        baseQuery: '',
        statuses: ['ACTIVE'],
        filters: {}
    }), []);

    const defaultEventConfigCriteria = useMemo(() => ({
        first: 0,
        rows: 25,
        sortField: 'eventType',
        sortOrder: 1,
        baseQuery: '',
        filters: {},
    }), []);

    const [eventConfigSearchCriteria, setEventConfigSearchCriteria] = useState(defaultEventConfigCriteria);

    const navigationContext = useNavigationContext(
        (params) => {
            setSearchCriteria(
                params?.searchCriteria
                    ? params.searchCriteria
                    : defaultCriteria
            );

            setEventConfigSearchCriteria(
                params?.eventConfigSearchCriteria
                    ? params.eventConfigSearchCriteria
                    : defaultEventConfigCriteria
            );
        }
    );

    const deactivateEventConfig = async () => {
        try {
            await EventConfigService.deactivate(deactivateDialogState.eventConfigId);
            showSuccessMessage(t('eventConfig.deactivateDialog.successMessage'));
            tableRef.current.refresh();
        } catch (error) {
            showErrorMessage(t('eventConfig.deactivateDialog.errorMessage'));
        }
        setDeactivateDialogState({ visible: false, eventConfigId: null });
    }
    const activateEventConfig = async () => {
        try {
            await EventConfigService.activate(activateDialogState.eventConfigId);
            showSuccessMessage(t('eventConfig.activateDialog.successMessage'));
            tableRef.current.refresh();
        } catch (error) {
            showErrorMessage(t('eventConfig.activateDialog.errorMessage'));
        }
        setActivateDialogState({ visible: false, eventConfigId: null });
    }
    const generateMenuItems = (row) => {
        
        const items = [];
        items.push({
            label: t('eventConfig.menu.edit'), 
            icon: 'edit', 
            key: `edit-${row.externalID}`, 
            command: e => {
                setSidebarState({ visible: true, eventConfigId: row.externalID });
            }
        });
        if (row.status === 'ACTIVE') {
        items.push({
            label: t('eventConfig.menu.deactivate'), 
            icon: 'deactivate', 
            separatorBefore: true,
            key: `deactivate-${row.externalID}`, 
            command: e => {
                setDeactivateDialogState({ visible: true, eventConfigId: row.externalID });
            }
        });
        }
        if (row.status === 'INACTIVE') {
        items.push({
            label: t('eventConfig.menu.activate'), 
            icon: 'activate', 
            separatorBefore: true,
            key: `activate-${row.externalID}`, 
            command: e => {
                setActivateDialogState({ visible: true, eventConfigId: row.externalID });
            }
        });
        }
        return items;
    };

    const navigationContextRef =
        useRef(navigationContext);

    useEffect(() => {
        navigationContextRef.current =
            navigationContext;
    }, [navigationContext]);

    useEffect(() => {
        navigationContextRef.current.putParam(
            'searchCriteria',
            searchCriteria
        );

        navigationContextRef.current.putParam(
            'eventConfigSearchCriteria',
            eventConfigSearchCriteria
        );
    }, [
        searchCriteria,
        eventConfigSearchCriteria
    ]);


    useEffect(() => {
        fetchEventTypeOptions();
        fetchTechnicianOptions();
        fetchEventPriorityOptions();
        fetchEventStatusOptions();
        fetchEventConfigTypeList();
        fetchEventConfigPriorityList();
        fetchEventConfigStatuses();
        fetchLocationOptions();
        fetchDeviceOptions();
    }, []);

    // Aktualizuj eventTypeOptions, eventPriorityOptions i eventStatusOptions gdy zmieni się język
    useEffect(() => {
        if (eventTypeOptions.length > 0) {
            setEventTypeOptions(prevTypeOptions => 
                prevTypeOptions.map(r => ({ id: r.id, label: t(`EventType.${r.id}`) }))
            );
        }
        if (eventPriorityOptions.length > 0) {
            setEventPriorityOptions(prevPriorityOptions => 
                prevPriorityOptions.map(r => ({ id: r.id, label: t(`EventPriority.${r.id}`) }))
            );
        }
        if (eventStatusOptions.length > 0) {
            setEventStatusOptions(prevStatusOptions => 
                prevStatusOptions.map(r => ({ id: r.id, label: t(`EventStatus.${r.id}`) }))
            );
        }
    }, [i18n.language]);


    const fetchLocationOptions = () => {
        LocationService.getAllLocations()
            .then(locations => {
                setLocationOptions(locations.map(l => ({ id: l.id, label: l.name })));
            })
            .catch(err => {
                showErrorMessage(t('events.error.fetchLocationOptions'));
            });
    }
    const fetchDeviceOptions = () => {
        DeviceService.getAllDevices()
            .then(devices => {
                setDeviceOptions(devices.map(d => ({ id: d.id, label: d.name })));
            })
            .catch(err => {
                showErrorMessage(t('events.error.fetchDeviceOptions'));
            });
    }

    const fetchEventTypeOptions = () => {
        EventService.getEventTypeList()
            .then(options => {
                setEventTypeOptions(options.map(o => ({ id: o, label: t(`EventType.${o}`) })));
            })
            .catch(err => {
                showErrorMessage(t('events.error.fetchEventTypeOptions'));
            });
    }
    const fetchEventPriorityOptions = () => {
        EventService.getEventPriorityList()
            .then(options => {
                setEventPriorityOptions(options.map(o => ({ id: o, label: t(`EventPriority.${o}`) })));
            })
            .catch(err => {
                showErrorMessage(t('events.error.fetchEventPriorityOptions'));
            });
    }
    const fetchEventStatusOptions = () => {
        EventService.getEventStatusList()
            .then(options => {
                setEventStatusOptions(options.map(o => ({ id: o, label: t(`EventStatus.${o}`) })));
            })
            .catch(err => {
                showErrorMessage(t('events.error.fetchEventStatusOptions'));
            });
    }
    const fetchEventConfigStatuses = () => {
        EventConfigService.getStatuses()
            .then(options => {
                setEventConfigStatuses(options.map(o => ({ id: o, label: t(`eventConfig.statuses.${o}`) })));
            })
            .catch(err => {
                showErrorMessage(t('eventConfigSidebar.error.fetchStatuses'));
            });
    }
    const fetchTechnicianOptions = () => {
        EventService.getTechnicianList()
            .then(options => {
                setTechnicianOptions(options.map(o => ({ id: o.id, label: o.name })));
            })
            .catch(err => {
                showErrorMessage(t('events.error.fetchTechnicianOptions'));
            });
    }

    const fetchEventConfigTypeList = () => {
        EventConfigService.getEventTypeList().then(types => {

                setEventConfigTypeList(types.map(type => ({ 
                    id: type, 
                    label: t(`EventType.${type}`) 
                })));
            
        }).catch(err => {
            showErrorMessage(t('eventConfigSidebar.error.fetchTypes'));
        });
    }

    const fetchEventConfigPriorityList = () => {
        EventConfigService.getEventPriorityList()
            .then(response => {
                const criticalities = response.map(c => ({ id: c.toString(), label: t(`eventConfig.priority.${c}`) }));
                setEventConfigPriorityList(criticalities);
            })
            .catch(err => {
                showErrorMessage(t('eventConfigSidebar.error.fetchPriorities'));
            });
    }

    const enums = useMemo(() => [
        {
            name: 'EventType',
            values: eventTypeOptions.map(r => ({key: r.id, value: r.label}))
        },
        {
            name: 'EventPriority',
            values: eventPriorityOptions.map(r => ({key: r.id, value: r.label}))
        },
        {
            name: 'EventStatus',
            values: eventStatusOptions.map(r => ({key: r.id, value: r.label}))
        }
    ], [eventTypeOptions, eventPriorityOptions, eventStatusOptions]);

    const normalizeEventCriteria = (criteria) => ({
        ...defaultCriteria,
        ...criteria,
        first: criteria?.first ?? defaultCriteria.first,
        rows: criteria?.rows ?? defaultCriteria.rows,
        sortField:
            !criteria?.sortField || criteria.sortField === 'null'
                ? defaultCriteria.sortField
                : criteria.sortField,
        sortOrder:
            criteria?.sortOrder === null || criteria?.sortOrder === undefined
                ? defaultCriteria.sortOrder
                : criteria.sortOrder,
        baseQuery: criteria?.baseQuery ?? defaultCriteria.baseQuery,
        statuses: criteria?.statuses ?? defaultCriteria.statuses,
        filters: criteria?.filters ?? defaultCriteria.filters,
    });

    const normalizeEventConfigCriteria = (criteria) => ({
        ...defaultEventConfigCriteria,
        ...criteria,
        first: criteria?.first ?? defaultEventConfigCriteria.first,
        rows: criteria?.rows ?? defaultEventConfigCriteria.rows,
        sortField:
            !criteria?.sortField || criteria.sortField === 'null'
                ? defaultEventConfigCriteria.sortField
                : criteria.sortField,
        sortOrder:
            criteria?.sortOrder === null || criteria?.sortOrder === undefined
                ? defaultEventConfigCriteria.sortOrder
                : criteria.sortOrder,
        baseQuery: criteria?.baseQuery ?? defaultEventConfigCriteria.baseQuery,
        filters: criteria?.filters ?? defaultEventConfigCriteria.filters,
    });

    const fetchData = async (criteria) => {
        try {
            return await EventService.getEvents(normalizeEventCriteria(criteria), enums);
        } catch (error) {
            showErrorMessage(t('events.error.fetchEvents'));
            return { data: [], totalRecords: 0 };
        }
    };

    const fetchEventConfigData = async (criteria) => {
        try {
            return await EventConfigService.getList(normalizeEventConfigCriteria(criteria));
        } catch (err) {
            showErrorMessage(t('eventConfigSidebar.error.fetchConfig'));
            return { data: [], totalRecords: 0 };
        }
    };

    const filterConfig = useMemo(() => [
        {
            fieldName: 'description',
            label: t('events.description'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'description'
        },
        {
            fieldName: 'eventType',
            label: t('events.eventType'),
            type: FILTER_TYPES.LIST,
            apiParamName: 'event_types',
            params: eventTypeOptions,
            apiValueConverter
        },
        {
            fieldName: 'location.name',
            label: t('events.location'),
            type: FILTER_TYPES.LIST,
            params: locationOptions,
            apiParamName: 'locations',
            apiValueConverter
        },
        {
            fieldName: 'device.name',
            label: t('events.device'),
            type: FILTER_TYPES.LIST,
            params: deviceOptions,
            apiParamName: 'devices',
            apiValueConverter
        },
        {
            fieldName: 'task.routes.systemId',
            label: t('events.routeId'),
            type: FILTER_TYPES.TEXT,
            apiParamName: 'route_id'
        },
        {
            fieldName: 'technician.name',
            label: t('events.technician'),
            type: FILTER_TYPES.LIST,
            apiParamName: 'technicians',
            params: technicianOptions,
            apiValueConverter
        },
        {
            fieldName: 'eventDateTime',
            label: t('events.eventDateTime'),
            type: FILTER_TYPES.DATE_TIME_RANGE,
            apiParamName: 'event_date_time',
            apiValueFromConverter,
            apiValueToConverter
        },
        {
            fieldName: 'priority',
            label: t('events.priority'),
            type: FILTER_TYPES.LIST,
            apiParamName: 'priorities',
            params: eventPriorityOptions,
            apiValueConverter
        },
        {
            fieldName: 'status',
            label: t('events.status'),
            type: FILTER_TYPES.LIST,
            apiParamName: 'statuses',
            params: eventStatusOptions.filter(o => searchCriteria.statuses.includes(o.id)),
            apiValueConverter
        },
    ], [eventTypeOptions, searchCriteria?.statuses, eventPriorityOptions, eventStatusOptions, locationOptions, deviceOptions, apiValueConverter]);

    const eventConfigFilterConfig = useMemo(() => {
        
        return [
            {
                fieldName: 'eventType',
                label: t('eventConfig.type'),
                type: FILTER_TYPES.LIST,
                params: eventConfigTypeList.length > 0 ? eventConfigTypeList : [],
                apiParamName: 'event_types',
                apiValueConverter: value => value.map(t => t.id).join(',')
            },
            {
                fieldName: 'threshold',
                label: t('eventConfig.treshold'),
                type: FILTER_TYPES.NUMBER_RANGE,
                apiParamName: 'threshold'
            },
            {
                fieldName: 'priority',
                label: t('eventConfig.priority_key'),
                type: FILTER_TYPES.LIST,
                params: eventConfigPriorityList.length > 0 ? eventConfigPriorityList : [],
                apiParamName: 'priorities',
                apiValueConverter: value => value.map(c => c.id).join(',')
            },
            {
                fieldName: 'generateServiceTask',
                label: t('eventConfig.generateServiceTask'),
                type: FILTER_TYPES.BOOLEAN,
                apiParamName: 'generate_service_task'
            },
            {
                fieldName: 'daysToArchive',
                label: t('eventConfig.daysToArchive'),
                type: FILTER_TYPES.NUMBER_RANGE,
                apiParamName: 'days_to_archive'
            },
            {
                fieldName: 'status',
                label: t('eventConfig.status'),
                type: FILTER_TYPES.LIST,
                params: eventConfigStatuses.length > 0 ? eventConfigStatuses : [],
                apiParamName: 'statuses',
                apiValueConverter: value => value.map(s => s.id).join(',')
            },
            {
                fieldName: 'displayOnErrorList',
                label: t('eventConfig.displayOnErrorList'),
                type: FILTER_TYPES.BOOLEAN,
                apiParamName: 'display_on_error_list'
            }
        ];
    }, [eventConfigTypeList, eventConfigPriorityList, eventConfigStatuses]);

    const getStatusesForTab = (index) => {
        if (index === 0) {
            return ['ACTIVE'];
        }

        if (index === 1) {
            return ['DONE', 'SKIPPED', 'CANCELLED'];
        }

        return ['ACTIVE'];
    };

    const handleEventsTabChange = useCallback(
        (index) => {
            const route =
                EVENTS_TAB_ROUTE_BY_INDEX[index];

            if (
                !route ||
                route === location.pathname
            ) {
                return;
            }

            navigate({
                pathname: route,
                search: location.search
            });
        },
        [
            location.pathname,
            location.search,
            navigate
        ]
    );

    useEffect(() => {
        /*
        * Configuration has its own table and criteria.
        */
        if (activeTab === 2) {
            return;
        }

        setSearchCriteria({
            ...defaultCriteria,
            first: 0,
            rows: 10,
            sortField: 'eventDateTime',
            sortOrder: -1,
            baseQuery: '',
            statuses: getStatusesForTab(activeTab),
            filters: {},
            timestamp: Date.now()
        });
    }, [
        activeTab,
        defaultCriteria,
        setSearchCriteria
    ]);

    const renderEventsTabHeader = useCallback(
        (
            index,
            label
        ) => {
            const route =
                EVENTS_TAB_ROUTE_BY_INDEX[index];

            return (options) => (
                <NavLink
                    to={{
                        pathname: route,
                        search: location.search
                    }}
                    role="tab"
                    aria-selected={options.selected}
                    aria-controls={options.ariaControls}
                    tabIndex={
                        options.selected ? 0 : -1
                    }
                    className={options.className}
                    onClick={(event) => {
                        if (
                            isModifiedLinkClick(event)
                        ) {
                            return;
                        }

                        event.preventDefault();
                        handleEventsTabChange(index);
                    }}
                >
                    {label}
                </NavLink>
            );
        },
        [
            handleEventsTabChange,
            location.search
        ]
    );

    const renderDataTable = () => (
        <DataTable
            key={`events-table-${activeTab}`}
            ref={tableRef}
            fetchData={fetchData}
            filterConfig={filterConfig}
            defaultSearchCriteria={{
                ...defaultCriteria,
                statuses: getStatusesForTab(activeTab),
            }}
            minWidth="1200px"
        >
            <Column field="eventDateTime" header={t('events.eventDateTime')} sortable sortField="eventDateTime" id="dateTime"
                headerClassName="dt2-header-wrap"
                body={row => RenderedHelper.formatTimestamp(row.eventDateTime)}
                style={{ minWidth: '140px', maxWidth: '160px' }}
            />
            <Column field="eventType" header={t('events.eventType')} sortable sortField="eventType" id="eventType"
                headerClassName="dt2-header-wrap"
                body={row => RenderedHelper.formatEnum(row.eventType, 'EventType')}
                style={{ minWidth: '130px', maxWidth: '160px' }}
            />
            <Column field="location.name" header={t('events.location')} sortable sortField="location.name" id="location"
                body={row => row.location?.name}
                style={{ minWidth: '140px', maxWidth: '180px' }}
            />
            <Column field="device.name" header={t('events.device')} sortable sortField="device.name" id="device"
                body={row => row.device?.name}
                style={{ minWidth: '130px', maxWidth: '170px' }}
            />
            <Column field="route.name" header={t('events.routeId')} sortable sortField="task.routes.systemId" id="routeId"
                headerClassName="dt2-header-wrap"
                body={row => row.route?.name}
                style={{ minWidth: '100px', maxWidth: '130px' }}
            />
            <Column field="technician.name" header={t('events.technician')} sortable sortField="technician.name" id="technician"
                body={row => row.technician?.name}
                style={{ minWidth: '130px', maxWidth: '170px' }}
            />
            <Column field="description" header={t('events.description')} sortable sortField="description" id="description"
                body={row => row.description}
                style={{ minWidth: '180px', maxWidth: '260px' }}
            />
            <Column field="priority" header={t('events.priority')} sortable sortField="priority" id="priority"
                body={row => RenderedHelper.formatEnum(row.priority, 'EventPriority')}
                style={{ minWidth: '110px', maxWidth: '140px' }}
            />
            <Column field="status" header={t('events.status')} sortable sortField="status" id="status"
                body={row => RenderedHelper.formatEnum(row.status, 'EventStatus')}
                style={{ minWidth: '110px', maxWidth: '140px' }}
            />
        </DataTable>
    );
        
    const eventConfigTable = (
        <DataTable
            ref={tableRef}
            fetchData={fetchEventConfigData}
            filterConfig={eventConfigFilterConfig}
            defaultSearchCriteria={defaultEventConfigCriteria}
            minWidth="1000px"
        >
            <Column field="eventType" header={t('eventConfig.type')} sortable sortField="eventType" id="eventType"
                headerClassName="dt2-header-wrap"
                body={row => RenderedHelper.formatEnum(row.eventType, 'EventType')}
                style={{ minWidth: '130px', maxWidth: '160px' }}
            />
            <Column field="threshold" header={t('eventConfig.threshold')} sortable sortField="threshold" id="threshold"
                body={row => row.threshold}
                style={{ minWidth: '110px', maxWidth: '130px' }}
            />
            <Column field="priority" header={t('eventConfig.priority_key')} sortable sortField="priority" id="priority"
                headerClassName="dt2-header-wrap"
                body={row => RenderedHelper.formatEnum(row.priority, 'EventPriority')}
                style={{ minWidth: '120px', maxWidth: '150px' }}
            />
            <Column field="generateServiceTask" header={t('eventConfig.generateServiceTask')} sortable sortField="generateServiceTask" id="generateServiceTask"
                headerClassName="dt2-header-wrap"
                body={row => t(`eventConfig.boolean.${row.generateServiceTask}`)}
                style={{ minWidth: '140px', maxWidth: '170px' }}
            />
            <Column field="displayOnErrorList" header={t('eventConfig.displayOnErrorList')} sortable sortField="displayOnErrorList" id="displayOnErrorList"
                headerClassName="dt2-header-wrap"
                body={row => t(`eventConfig.boolean.${row.displayOnErrorList}`)}
                style={{ minWidth: '140px', maxWidth: '170px' }}
            />
            <Column field="daysToArchive" header={t('eventConfig.daysToArchive')} sortable sortField="daysToArchive" id="daysToArchive"
                headerClassName="dt2-header-wrap"
                body={row => row.daysToArchive}
                style={{ minWidth: '120px', maxWidth: '150px' }}
            />
            <Column field="status" header={t('eventConfig.status')} sortable sortField="status" id="status"
                body={row => RenderedHelper.formatEnum(row.status, 'eventConfig.statuses')}
                style={{ minWidth: '120px', maxWidth: '150px' }}
            />
            {isUserInAnyRole(['EVENTS_WRITE']) && (
                <Column
                    className="dt2-actions-column"
                    headerClassName="dt2-actions-column"
                    bodyClassName="dt2-actions-column"
                    body={row => (
                        <ActionButton
                            menuRef={menuRef}
                            generateMenuItems={() => generateMenuItems(row)}
                            showMainNavigation={false}
                        />
                    )}
                />
            )}
        </DataTable>
    );



        
    return (
        <div className={`events-container events-container--tab-${activeTab}`}>
            <ActionMenu ref={menuRef} />

            <TabView
                activeIndex={activeTab}
                onTabChange={(event) => {
                    handleEventsTabChange(event.index);
                }}
                renderActiveOnly
            >
                <TabPanel
                    header={t('events.tabs.active')}
                    headerTemplate={renderEventsTabHeader(
                        0,
                        t('events.tabs.active')
                    )}
                >
                    <div className="data-card">
                        {renderDataTable()}
                    </div>
                </TabPanel>

                <TabPanel
                    header={t('events.tabs.archive')}
                    headerTemplate={renderEventsTabHeader(
                        1,
                        t('events.tabs.archive')
                    )}
                >
                    <div className="data-card">
                        {renderDataTable()}
                    </div>
                </TabPanel>

                <TabPanel
                    header={t('events.tabs.config')}
                    headerTemplate={renderEventsTabHeader(
                        2,
                        t('events.tabs.config')
                    )}
                >
                    <div className="data-card">
                        {eventConfigTable}
                    </div>

                    <EventConfigSidebar
                        visible={sidebarState.visible}
                        onHide={() => {
                            setSidebarState({
                                visible: false,
                                eventConfigId: null
                            });
                        }}
                        onSuccess={() => {
                            tableRef.current?.refresh();

                            setSidebarState({
                                visible: false,
                                eventConfigId: null
                            });
                        }}
                        eventConfigId={
                            sidebarState.eventConfigId
                        }
                    />
                </TabPanel>
            </TabView>
            <ConfirmDialog 
                visible={deactivateDialogState.visible}
                onHide={() => setDeactivateDialogState({ visible: false, eventConfigId: null })}
                onConfirm={() => deactivateEventConfig()}
                title={t('eventConfig.deactivateDialog.title')}
                message={t('eventConfig.deactivateDialog.message')}
                actionButtonLabel={t('eventConfig.deactivateDialog.confirmButtonLabel')}
                variant={"danger"}
                delayedConfirm={true}
            />
            <ConfirmDialog 
                visible={activateDialogState.visible}
                onHide={() => setActivateDialogState({ visible: false, eventConfigId: null })}
                onConfirm={() => activateEventConfig()}
                title={t('eventConfig.activateDialog.title')}
                message={t('eventConfig.activateDialog.message')}
                actionButtonLabel={t('eventConfig.activateDialog.confirmButtonLabel')}
                variant={"success"}
                delayedConfirm={true}
            />
        </div>
    )
}

export default Events;